import React, { useEffect, useState } from "react";
import { formatWords, keyToTitle } from "../../../../../utils";
import { Backdrop, Button, Chip, InfoText, InfoTimeCardAdv, InfoWrapper, LogoLoader, NewTab, StatusTags } from "../../../../../components";
import { formatWords2 } from "../../../../../utils/stringUtils";
import { useNavigate } from "react-router-dom";
import Info from "./AiJobDetail/Info";
import Candidates from "./AiJobDetail/Candidates";
import CallScheduleList from "./AiJobDetail/CallScheduleList";
import AiCallHistory from "./AiJobDetail/AiCallHistory";
import { fetchJobCardCounts, fetchJobPost, talentMatchCountPercentage } from "../../../../../redux/slices/bdmjobs/bdmjobsApi";
import { convertFromUTC } from "../../../../../utils/dateUtils";
import { getTotalCountApi } from "../../../../../redux/slices/totalCount/totalCountApi";
import InternalSubmissions from "./AiJobDetail/InternalSubmissions";
import { fetchAllAiCallsApi } from "../../../../../redux/slices/aicalls/aicallsApi";
import JobBoardCandidates from "./AiJobDetail/JobBoardCandidates";

const AiJobDetails = () => {
  const [jobDetails, setJobDetails] = useState(null);
  const [talentMatchData, setTalentMatchData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [talentMatchCount, setTalentMatchCount] = useState(0);
  const [aiCallsCount, setAiCallsCount] = useState(0);
  const [interestedCount, setInterestedCount] = useState(0);
  const [notInterestedCount, setNotInterestedCount] = useState(0);
  const navigate = useNavigate();

  let uiid = sessionStorage.getItem("selectedJobUiid");
  const jobInfo = jobDetails?.jobInfo || {};
  const location = jobInfo?.location || {};
  const [jobCardCounts, setJobCardCounts] = useState({
    shortlistedCount: 0,
    pipelineCount: 0,
    submittedCount: 0,
    confirmedCount: 0,
    interviewCount: 0,
  });

  const JobPostingStatus = {
    1: "Draft",
    2: "Active",
    3: "On Hold",
    4: "Hold by Client",
    5: "Interview L1",
    6: "Interview L2",
    7: "Interview L3",
    8: "Priority",
    9: "Filled",
    10: "Closed",
    11: "Agreement Sent",
    12: "Agreement Accepted",
  };

  const latestStatusCode =
    jobDetails?.statusHistory?.length > 0 &&
    jobDetails.statusHistory[jobDetails.statusHistory.length - 1].status;

  const latestStatus = JobPostingStatus[latestStatusCode] || "Active";

  const getJobCardCounts = async () => {
    try {
      const response = await fetchJobCardCounts({
        coll: "JobPosting",
        fil: JSON.stringify({
          uiid: { type: "SUBMISSION_STATUS_COUNT", value: Number(uiid) },
        }),
        useAggregate: true,
      });

      if (response?.data?.data?.[0]) {
        setJobCardCounts(response.data.data?.[0]);
      }
    } catch (err) {
      console.error("Error fetching job card counts:", err);
    }
  };

  const getJobDetails = async () => {
    try {
      const response = await fetchJobPost({
        coll: "JobPosting",
        // ret: "uiid jobInfo skills orgInform jobId status addedOn addedBy",
        // ref: "Job Note Submission",
        ref: "Job NotifyTo",
        extret: "uiid firstName role userType",
        fil: { uiid: { type: "NONE", value: uiid } },
        extincl: "uiid name userType",
        // incl: ["", "uiid statusHistory.status statusHistory.addedBy statusHistory.addedOn candidateId addedBy"]
      });

      if (response?.data) {
        setJobDetails(response?.data?.data);
      }
    } catch (err) {
      console.error("Error fetching job details:", err);
    } finally {
      setLoading(false);
    }
  };

  const getTalentPercentage = async () => {
    try {
      const response = await talentMatchCountPercentage({
        coll: "TalentMatche",
        fil: JSON.stringify({
          jobPostId: {
            type: "TM_SKILLMATCHPERCENT_COUNT",
            value: Number(uiid),
          },
        }),
        useAggregate: true,
      });

      if (response?.data?.data?.[0]) {
        const res = response?.data.data[0];
        const total = res?.totalCount || 0;

        const talentMatch = [
          {
            count: `${res["80-100"]}/${total}`,
            percentage: "80-100%",
            color: "primary",
          },
          {
            count: `${res["50-80"]}/${total}`,
            percentage: "50-80%",
            color: "info",
          },
          {
            count: `${res["0-50"]}/${total}`,
            percentage: "< 50%",
            color: "leave",
          },
        ];

        setTalentMatchData(talentMatch);
      }
    } catch (err) {
      console.error("Error fetching Talent Match count:", err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // months are 0-based
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const getDaysSinceAdded = (dateString) => {
    if (!dateString) return null;
    const createdDate = new Date(dateString);
    const today = new Date();

    // Calculate difference in days
    const diffTime = today - createdDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Opened today";
    if (diffDays === 1) return "Opened 1 day ago";
    return `Opened ${diffDays} days ago`;
  };

  const fetchAiCalls = async () => {
    try {
      setLoading(true);
      const payload = {
        coll: "AiCall",
        fil: {
          jobPostId: { type: "NONE", value: uiid },
          status: { type: "IN", value: [2, 3, 4, 6, 7, 8] }
        },
        ret: "status uiid"
      }
      const response = await fetchAllAiCallsApi(payload);
      if (response?.success) {
        setAiCallsCount(response?.data?.data?.length);
        setInterestedCount(response?.data?.data?.filter((call) => call.status == 7).length);
        setNotInterestedCount(response?.data?.data?.filter((call) => call.status == 8).length);
      }
    } catch (error) {
      console.error("Error fetching AI Calls:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchTalentMatchCount = async () => {
      try {
        if (jobDetails?.talentMatch?.ids?.length > 0) {
          const res = await getTotalCountApi({
            collection: "talentmatches",
            coll: "TalentMatche",
            app: "ats",
            filters: {
              uiid: { type: "IN", value: jobDetails.talentMatch.ids },
            },
          });
          setTalentMatchCount(res?.data?.data || 0);
        }
      } catch (error) {
        console.error("Error fetching Talent Match count:", error);
      }
    };
    fetchTalentMatchCount();
  }, [jobDetails]);

  useEffect(() => {
    if (!uiid) {
      setLoading(false);
      if (window.history.state && window.history.state.idx > 0) {
        navigate(-1);
      } else {
        navigate("/ats/jobs/?jobs=ai-recruiter-jobs");
      }
      return;
    }
    if (uiid) {
      getJobDetails();
      getJobCardCounts();
      getTalentPercentage();
      fetchAiCalls();
    }
  }, [uiid]);

  let generalJobDetails = [
    {
      "Job Title": formatWords2(jobDetails?.jobId?.title || "N/A"),
      Location: `${formatWords2(location?.city || "N/A")}, ${formatWords2(location?.state || "N/A")}`,
      Position: jobDetails?.jobInfo?.type || "N/A",
      "Position Opened": jobDetails?.jobInfo?.reqHourWeek || "N/A", // doubt
      "Position Closed": jobDetails?.jobInfo?.duration || "N/A", // doubt
    },
  ];

  let secondaryDetails = [
    {
      "Show Created by": formatWords2(jobDetails?.addedBy?.firstName || "N/A"),
      "Modified by": formatWords2(
        jobDetails?.jobId?.modifiedBy?.firstName || "N/A",
      ),
      "Created on": jobDetails?.addedOn
        ? formatDate(convertFromUTC(jobDetails?.addedOn))
        : "N/A",
      "Modified On": jobDetails?.modifiedOn
        ? formatDate(convertFromUTC(jobDetails?.modifiedOn))
        : "N/A",
      "Talent Match(Count of Candidate)": talentMatchData || [
        { count: "0", percentage: "80-100%", color: "primary" },
        { count: "0", percentage: "50-80%", color: "info" },
        { count: "0", percentage: "< 50%", color: "leave" },
      ],
    },
  ];

  const infoCardDetails = [
    { title: "Candidates Found", value: talentMatchCount || 0 },
    { title: "AI Calls Made", value: aiCallsCount || 0 },
    { title: "Interested", value: interestedCount || 0 },
    { title: "Not Interested", value: notInterestedCount || 0 },
  ];

  const jobDetailMenus=[
    {
      title: "Info",
      page: <Info jobDetails={jobDetails} />,
      type: "main",
      id: "aijobsdetailsuiid_info",
    },
    {
      title: "Talent Match",
      page: <Candidates jobPostId={uiid} jobDetails={jobDetails} />,
      type: "main",
      id: "aijobsdetailsuiid_candidates",
    },
    {
      title: "Job Board Candidates",
      page: <JobBoardCandidates jobPostId={uiid} jobDetails={jobDetails} />,
      type: "main",
      id: "aijobsdetailsuiid_candidates",
    },
    {
      title: "Call Schedule List",
      page: <CallScheduleList jobPostId={jobDetails?.uiid} />,
      type: "main",
      id: "aijobsdetailsuiid_call_schedule_list",
    },
    {
      title: "AI Call History",
      page: <AiCallHistory jobPostId={jobDetails?.uiid} />,
      type: "main",
      id: "aijobsdetailsuiid_ai_call_history",
    },
    {
      title: "Internal Submissions",
      page: <InternalSubmissions jobPostId={jobDetails?.uiid} notifyTo={jobDetails?.notifyTo} />,
      type: "main",
      id: "aijobsdetailsuiid_internal_submissions",
    },    
  ]
  return (
    <div className="flex flex-col gap-5 flex-1">
      <div className="flex justify-between items-center mr-4 mt-3">
        <Button
          title="Job Post Details"
          color="black"
          variant="text"
          size="md"
          fromLeft
          icon="arrow_left_alt"
          className="self-start text-md font-semibold px-0!"
          onClick={() => {
            navigate("/ats/jobs/?jobs=ai-recruiter-jobs");
          }}
          // onClick={() => {
          //   if (window.history.state && window.history.state.idx > 0) {
          //     navigate(-1);
          //   } else {
          //     navigate("/ats/jobs?jobs=all-jobs");
          //   }
          // }}
          //   onClick={() => {
          //     if (prevPage) {
          //       if (prevPage?.includes("applicants/applicant-details")) {
          //         navigate(prevPage, {state: {applicantId}})
          //       } else {
          //         navigate(prevPage);
          //       }
          //     } else {
          //       navigate("/ats/jobs?jobs=all-jobs");
          //     }
          //   }}
        />
        {/* <div className="flex gap-2">
          {user?.data?.role?.type === 3 && jobDetails?.addedBy?.uiid === user?.data?.uiid &&
            <Button
              variant="outlined"
              title={"Edit"}
              icon={"edit_square"}
              onClick={() => setOpenEditModal(true)}
            />
          }

          <Button title={"Job Boards"} onClick={handleJobBoardsClick} />
        </div> */}
      </div>

      <div className="flex justify-between items-center gap-1 ">
        <div className="flex gap-2 items-center">
          <h6 className="subtitle-reg ml-1 mr-1">
            {/* {formatWords("NESU-070433-729843286906423")} */}
            {formatWords(jobInfo?.jobCode)}
          </h6>
          <p className=" text-xs font-[600] text-gray-400 mr-3">
            {/* (Opened 3 days ago) */}
            ({getDaysSinceAdded(jobDetails?.addedOn)})
          </p>
          <StatusTags
            icon
            size="lg"
            tag={latestStatus}
          />
        </div>
      </div>
      <div className=" grid grid-cols-4 md:grid-cols-3 lg:grid-cols-5 gap-5 my-1 inner-section px-4 py-6 rounded">
        {Object.entries(generalJobDetails[0]).map(([key, value]) => {
          return (
            <InfoText
              size="sm"
              key={key}
              title={keyToTitle(key)}
              value={value}
            />
          );
        })}
      </div>
      <div className="flex gap-5 my-1 inner-section p-4 rounded">
        <div className="grid md:grid-cols-2 xl:grid-cols-4 lg:grid-cols-3 gap-5">
          {Object.entries(secondaryDetails[0]).map(([key, value]) => {
            const isTalentMatch = key === "Talent Match(Count of Candidate)";
            const displayValue = isTalentMatch ? (
              <div className="flex gap-3">
                {value.map((data, index) => (
                  <div className="flex flex-col gap-2 items-center" key={index}>
                    <Chip
                      key={index}
                      label={data.count}
                      color={data.color}
                      variant="outlined"
                      size="lg"
                      className={`!px-4 py-3 rounded-xl bg-${data?.color}-50 !text-xs`}
                    />
                    <div className="text-xs">{data?.percentage}</div>
                  </div>
                ))}
              </div>
            ) : (
              value
            );

            return (
              <div className={isTalentMatch ? "col-span-3" : ""} key={key}>
                <InfoText
                  size="sm"
                  title={keyToTitle(key)}
                  value={displayValue}
                />
              </div>
            );
          })}
        </div>
      </div>

      <InfoWrapper>
        {infoCardDetails.map((item) => (
          <InfoTimeCardAdv
            title={item.title}
            value={item.value}
            variant={"text"}
            color={"primary"}
            size="sm"
          />
        ))}
      </InfoWrapper>

      <div className="flex-1 rounded-xl">
        <NewTab
          tabs={jobDetailMenus}
          color="gray"
          variant="text"
          activeVariant="text"
          mode="url"
          tabId="job-details"
        />
      </div>

      {/* <Modal
        isOpen={openEditModal}
        onClose={() => setOpenEditModal(false)}
        title={"Edit"}
        titleColor={"primary"}
        // width="max-w-4xl"
        width="w-full"
      >
        
        <AddJobPost
          onCancel={() => setOpenEditModal(false)}
          editData={jobDetails}
          onSuccess={() => {
            setOpenEditModal(false);
            getJobDetails();
          }}
        />
      </Modal> */}
      <Backdrop isOpen={loading}>
        <LogoLoader />
      </Backdrop>
    </div>
  );
};

export default AiJobDetails;
